import React, { useEffect, useState } from 'react'
import { interceptExternalLinkClick } from '../../lib/nativeShell'
import { Link } from 'react-router-dom'
import {
    Checkin,
    CollectionItemWithCollection,
    getCheckins,
} from '../../api/client'
import CheckinItem from '../CheckinItem'
import PersonAvatar from '../PersonAvatar'
import {
    PersonLikeData,
    formatPersonDisplayName,
} from '../../utils/people'

/**
 * Read-only viewer for a Person collection item. Mirrors LocationDetailModal
 * in shape (header / body / recent checkins / View all link). Surfaces the
 * five most recent checkins that reference this person plus a deep link into
 * `CheckinsPage?collection_item=<id>` for the rest.
 *
 * Whether an Edit button appears is the caller's call, via `onEdit`. The
 * surfaces you reach a person from while working on their data — the
 * timeline chip strip, the composer's list editor, `PeopleCollectionPage` —
 * all pass it, routing to `EditCollectionItemById`. The browsing surfaces
 * (`CollectionsPage`'s rail, `ProjectPeopleSection`) leave it off and stay
 * read-only.
 */

const RECENT_LIMIT = 5

export interface PersonDetailValue {
    collectionItemId: number
    /** Full data payload from the page-level cache. The modal reads display
     *  name, contact info, picture, etc. directly off of this — there's no
     *  follow-up fetch for the row itself, only for its recent checkins. */
    data: PersonLikeData
}

interface PersonDetailModalProps {
    open: boolean
    value: PersonDetailValue | null
    /** When set, an Edit button appears in the footer. Callers that own an
     *  edit path pass it; read-only browsing surfaces omit it. */
    onEdit?: () => void
    onClose: () => void
}

const PersonDetailModal: React.FC<PersonDetailModalProps> = ({
    open,
    value,
    onEdit,
    onClose,
}) => {
    const [recent, setRecent] = useState<Checkin[]>([])
    const [recentCollectionItems, setRecentCollectionItems] = useState<
        Record<string, CollectionItemWithCollection>
    >({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!open || !value) {
            setRecent([])
            setRecentCollectionItems({})
            setError(null)
            return
        }
        const collectionItemId = value.collectionItemId
        let cancelled = false
        setLoading(true)
        setError(null)
        getCheckins(undefined, undefined, RECENT_LIMIT, undefined, 'most_recent', undefined, undefined, collectionItemId)
            .then((response) => {
                if (cancelled) return
                setRecent(response.checkins)
                setRecentCollectionItems(response.collection_items || {})
            })
            .catch((err) => {
                if (cancelled) return
                console.error('Failed to load recent checkins for person:', err)
                setError('Failed to load recent checkins.')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => { cancelled = true }
    }, [open, value])

    if (!open || !value) return null

    const data = value.data || {}
    const displayName = formatPersonDisplayName(data)
    const nickname = typeof data.nickname === 'string' ? data.nickname.trim() : ''
    const relation = typeof data.relation === 'string' ? data.relation.trim() : ''
    const notes = typeof data.notes === 'string' ? data.notes.trim() : ''
    const phone = typeof data.phone === 'string' ? data.phone.trim() : ''
    const email = typeof data.email === 'string' ? data.email.trim() : ''
    const instagram = typeof data['Instagram handle'] === 'string' ? data['Instagram handle']!.trim() : ''
    const website = typeof data.website === 'string' ? data.website.trim() : ''

    const hasContactBlock = !!(phone || email || instagram || website)
    const hasMetaBlock = !!(relation || hasContactBlock || notes)

    return (
        <Backdrop onClose={onClose}>
            <div
                className="bg-white rounded-lg shadow-xl flex flex-col w-[640px] max-w-[95vw] max-h-[90vh] overflow-hidden"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
                    <div className="min-w-0 flex-1 pr-4 flex items-center gap-3">
                        <PersonAvatar
                            data={data}
                            className="h-10 w-10"
                            initialsClassName="text-sm"
                            alt={displayName}
                        />
                        <div className="min-w-0">
                            <h3 className="text-sm font-medium text-gray-900 truncate">
                                {displayName}
                                {nickname && (
                                    <span className="ml-1.5 text-gray-500 font-normal">({nickname})</span>
                                )}
                            </h3>
                            {relation && (
                                <div className="text-xs text-gray-500 truncate">{relation}</div>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 shrink-0"
                        aria-label="Close"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {hasMetaBlock && (
                        <div className="px-4 py-3 border-b border-gray-100 space-y-2">
                            {hasContactBlock && (
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                                    {phone && (
                                        <a
                                            href={`tel:${phone}`}
                                            className="text-amber-800 hover:text-amber-900"
                                        >
                                            📞 {phone}
                                        </a>
                                    )}
                                    {email && (
                                        <a
                                            href={`mailto:${email}`}
                                            className="text-amber-800 hover:text-amber-900 truncate"
                                        >
                                            ✉️ {email}
                                        </a>
                                    )}
                                    {instagram && (
                                        <a
                                            href={`https://instagram.com/${instagram.replace(/^@/, '')}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={interceptExternalLinkClick}
                                            className="text-amber-800 hover:text-amber-900"
                                        >
                                            📷 @{instagram.replace(/^@/, '')}
                                        </a>
                                    )}
                                    {website && (
                                        <a
                                            href={website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={interceptExternalLinkClick}
                                            className="text-amber-800 hover:text-amber-900 truncate"
                                        >
                                            🔗 {website}
                                        </a>
                                    )}
                                </div>
                            )}
                            {notes && (
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{notes}</p>
                            )}
                        </div>
                    )}

                    <div className="px-4 py-4">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                            Recent checkins
                        </h4>
                        {loading ? (
                            <div className="flex justify-center py-6">
                                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-amber-500" />
                            </div>
                        ) : error ? (
                            <p className="text-sm text-rose-500">{error}</p>
                        ) : recent.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">No checkins yet for this person.</p>
                        ) : (
                            <div>
                                {recent.map((c, idx) => (
                                    <CheckinItem
                                        key={c.id}
                                        checkin={c}
                                        index={idx}
                                        showSeparator={idx > 0}
                                        editingCheckinId={null}
                                        showNewCheckinComposer={false}
                                        features={[]}
                                        recentFeatures={[]}
                                        sortOrder="most_recent"
                                        savingState={null}
                                        showMenu={false}
                                        collectionItems={recentCollectionItems}
                                        formatDate={(d) => new Date(d).toLocaleDateString()}
                                        getComposerCheckinFromCheckin={() => ({ body: '', applies_to: '', features: [], projects: [] })}
                                        handleFieldEdit={() => {}}
                                        handleDeleteCheckin={() => {}}
                                        handleEditCheckin={() => {}}
                                        handleDuplicateCheckin={() => {}}
                                        handleFilterSelect={() => {}}
                                        handleReanalyzeCheckin={() => {}}
                                        setEditingCheckinId={() => {}}
                                        setCheckins={() => {}}
                                        setShowNewCheckinComposer={() => {}}
                                        firstElementIsEmptyCheckin={() => false}
                                        isSelectionMode={false}
                                        isSelected={false}
                                        onToggleSelection={() => {}}
                                    />
                                ))}
                            </div>
                        )}
                        <div className="mt-3">
                            <Link
                                to={`/checkins?collection_item=${value.collectionItemId}`}
                                onClick={onClose}
                                className="text-sm text-amber-800 hover:text-amber-900"
                            >
                                View all checkins →
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2 flex-shrink-0">
                    {onEdit && (
                        <button
                            onClick={onEdit}
                            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
                        >
                            Edit
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm bg-action text-plum rounded hover:bg-action-dark"
                    >
                        Close
                    </button>
                </div>
            </div>
        </Backdrop>
    )
}

const Backdrop: React.FC<{ onClose: () => void; children: React.ReactNode }> = ({
    onClose,
    children,
}) => (
    <div
        className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={onClose}
    >
        {children}
    </div>
)

export default PersonDetailModal
